import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Switch, InputNumber, Space, Typography, Tooltip, message } from 'antd';
import {
    EditOutlined,
    ClearOutlined,
    HighlightOutlined,
    LineOutlined,
    BorderOutlined,
    SaveOutlined,
    ScissorOutlined,
    UndoOutlined,
    SwapOutlined,
    DownloadOutlined,
    CopyOutlined
} from '@ant-design/icons';

const { Text } = Typography;

// Tool definitions
const GOO_TOOLS = {
    DRAW: 'draw',
    ERASE: 'erase',
    FILL: 'fill',
    LINE: 'line',
    RECT: 'rect',
    ELLIPSE: 'ellipse'
};

/**
 * GooEditorPanel — A mini grid-based goo shape editor rendered in a side drawer.
 * Users draw pixel-grid shapes, apply a goo (metaball) SVG filter, and export as SVG.
 *
 * Props:
 *  - visible: boolean — controls drawer visibility
 *  - onClose: () => void
 *  - onSaveSVG: (svgString, svgDataUrl) => void — called when user saves the goo SVG
 *  - onSaveToPunches: (svgString, thumbnailDataUrl) => void — save to persistent punch list
 */
const GooEditorPanel = ({ visible, onClose, onSaveSVG, onSaveToPunches }) => {
    const canvasRef = useRef(null);
    const gooSvgRef = useRef(null);
    const containerRef = useRef(null);

    // Grid state
    const [gridW, setGridW] = useState(16);
    const [gridH, setGridH] = useState(16);
    const [cellSize, setCellSize] = useState(20);
    const gridRef = useRef([]);

    // Tool state
    const [currentTool, setCurrentTool] = useState(GOO_TOOLS.DRAW);
    const isDrawingRef = useRef(false);
    const startCellRef = useRef(null);
    const previewCellsRef = useRef([]);

    // Params
    const [tileRoundness, setTileRoundness] = useState(0);
    const [armThickness, setArmThickness] = useState(50);
    const [diagBridges, setDiagBridges] = useState(true);
    const [diagBlobSize, setDiagBlobSize] = useState(15);
    const [gooBlur, setGooBlur] = useState(10);
    const [gooContrast, setGooContrast] = useState(43);
    const [gooBias, setGooBias] = useState(-20);
    const [showGrid, setShowGrid] = useState(true);
    const [cropExport, setCropExport] = useState(true);

    // Undo stack
    const undoStackRef = useRef([]);

    // Initialize grid
    const initGrid = useCallback((w, h) => {
        const grid = [];
        for (let y = 0; y < h; y++) {
            grid[y] = [];
            for (let x = 0; x < w; x++) {
                grid[y][x] = 0;
            }
        }
        gridRef.current = grid;
    }, []);

    // Save current grid to undo stack
    const pushUndo = useCallback(() => {
        const snapshot = gridRef.current.map(row => [...row]);
        undoStackRef.current.push(snapshot);
        if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    }, []);

    // Undo
    const undo = useCallback(() => {
        if (undoStackRef.current.length === 0) return;
        gridRef.current = undoStackRef.current.pop();
        render();
    }, []);

    // Resize canvas element
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const svg = gooSvgRef.current;
        if (!canvas || !svg) return;

        const w = gridW * cellSize;
        const h = gridH * cellSize;
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        svg.setAttribute('width', w);
        svg.setAttribute('height', h);
        svg.style.width = w + 'px';
        svg.style.height = h + 'px';
    }, [gridW, gridH, cellSize]);

    // Round rect helper
    const roundRect = (ctx, x, y, w, h, r) => {
        r = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    };

    // Build SVG path data from grid
    const buildPathData = useCallback(() => {
        const grid = gridRef.current;
        const gW = gridW;
        const gH = gridH;
        const cs = cellSize;
        let pathD = '';

        // Tiles
        for (let y = 0; y < gH; y++) {
            for (let x = 0; x < gW; x++) {
                if (grid[y] && grid[y][x]) {
                    const r = tileRoundness * cs / 100;
                    const px = x * cs, py = y * cs;
                    if (r > 0) {
                        const rr = Math.min(r, cs / 2);
                        pathD += `M${px + rr},${py} L${px + cs - rr},${py} Q${px + cs},${py} ${px + cs},${py + rr} L${px + cs},${py + cs - rr} Q${px + cs},${py + cs} ${px + cs - rr},${py + cs} L${px + rr},${py + cs} Q${px},${py + cs} ${px},${py + cs - rr} L${px},${py + rr} Q${px},${py} ${px + rr},${py} Z `;
                    } else {
                        pathD += `M${px},${py} h${cs} v${cs} h${-cs} Z `;
                    }
                }
            }
        }

        // Arms
        const armW = armThickness / 100 * cs;
        const offset = (cs - armW) / 2;
        for (let y = 0; y < gH; y++) {
            for (let x = 0; x < gW; x++) {
                if (!grid[y] || !grid[y][x]) continue;
                if (x + 1 < gW && grid[y][x + 1]) {
                    pathD += `M${x * cs + cs - 1},${y * cs + offset} h2 v${armW} h-2 Z `;
                }
                if (y + 1 < gH && grid[y + 1] && grid[y + 1][x]) {
                    pathD += `M${x * cs + offset},${y * cs + cs - 1} v2 h${armW} v-2 Z `;
                }
                if (diagBridges) {
                    const blobR = diagBlobSize * cs / 200;
                    if (x + 1 < gW && y + 1 < gH && grid[y + 1][x + 1] && !grid[y][x + 1] && !grid[y + 1][x]) {
                        const cx = (x + 1) * cs, cy = (y + 1) * cs;
                        pathD += `M${cx},${cy - blobR} A${blobR},${blobR} 0 1,1 ${cx},${cy + blobR} A${blobR},${blobR} 0 1,1 ${cx},${cy - blobR} Z `;
                    }
                    if (x - 1 >= 0 && y + 1 < gH && grid[y + 1][x - 1] && !grid[y][x - 1] && !grid[y + 1][x]) {
                        const cx = x * cs, cy = (y + 1) * cs;
                        pathD += `M${cx},${cy - blobR} A${blobR},${blobR} 0 1,1 ${cx},${cy + blobR} A${blobR},${blobR} 0 1,1 ${cx},${cy - blobR} Z `;
                    }
                }
            }
        }
        return pathD;
    }, [gridW, gridH, cellSize, tileRoundness, armThickness, diagBridges, diagBlobSize]);

    // Update SVG overlay
    const updateGooSVG = useCallback(() => {
        const svg = gooSvgRef.current;
        if (!svg) return;
        const pathD = buildPathData();
        const slope = gooContrast;
        const intercept = gooBias / 100;

        svg.innerHTML = `
            <defs>
                <filter id="goo-editor-filter" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="${gooBlur}" result="blur"/>
                    <feComponentTransfer in="blur" result="goo">
                        <feFuncA type="linear" slope="${slope}" intercept="${intercept}"/>
                    </feComponentTransfer>
                </filter>
            </defs>
            <g filter="url(#goo-editor-filter)">
                <path d="${pathD}" fill="#000"/>
            </g>
        `;
    }, [buildPathData, gooBlur, gooContrast, gooBias]);

    // Render canvas
    const render = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const grid = gridRef.current;
        const gW = gridW;
        const gH = gridH;
        const cs = cellSize;
        const w = canvas.width, h = canvas.height;

        ctx.clearRect(0, 0, w, h);

        // Filled cells
        for (let y = 0; y < gH; y++) {
            for (let x = 0; x < gW; x++) {
                if (grid[y] && grid[y][x]) {
                    ctx.fillStyle = '#000';
                    const r = tileRoundness * cs / 100;
                    const px = x * cs, py = y * cs;
                    roundRect(ctx, px, py, cs, cs, r);
                    ctx.fill();
                }
            }
        }

        // Arms
        const armW = armThickness / 100 * cs;
        const offset = (cs - armW) / 2;
        for (let y = 0; y < gH; y++) {
            for (let x = 0; x < gW; x++) {
                if (!grid[y] || !grid[y][x]) continue;
                if (x + 1 < gW && grid[y][x + 1]) {
                    ctx.fillStyle = '#000';
                    ctx.fillRect(x * cs + cs - 1, y * cs + offset, 2, armW);
                }
                if (y + 1 < gH && grid[y + 1] && grid[y + 1][x]) {
                    ctx.fillStyle = '#000';
                    ctx.fillRect(x * cs + offset, y * cs + cs - 1, armW, 2);
                }
                if (diagBridges) {
                    const blobR = diagBlobSize * cs / 200;
                    if (x + 1 < gW && y + 1 < gH && grid[y + 1][x + 1] && !grid[y][x + 1] && !grid[y + 1][x]) {
                        ctx.beginPath();
                        ctx.arc((x + 1) * cs, (y + 1) * cs, blobR, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    if (x - 1 >= 0 && y + 1 < gH && grid[y + 1][x - 1] && !grid[y][x - 1] && !grid[y + 1][x]) {
                        ctx.beginPath();
                        ctx.arc(x * cs, (y + 1) * cs, blobR, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        // Preview cells (shape tools)
        if (previewCellsRef.current.length > 0) {
            ctx.fillStyle = currentTool === GOO_TOOLS.ERASE ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.3)';
            for (const [px, py] of previewCellsRef.current) {
                ctx.fillRect(px * cs, py * cs, cs, cs);
            }
        }

        // Grid lines
        if (showGrid) {
            ctx.strokeStyle = 'rgba(0,0,0,0.12)';
            ctx.lineWidth = 0.5;
            for (let x = 0; x <= gW; x++) {
                ctx.beginPath();
                ctx.moveTo(x * cs, 0);
                ctx.lineTo(x * cs, h);
                ctx.stroke();
            }
            for (let y = 0; y <= gH; y++) {
                ctx.beginPath();
                ctx.moveTo(0, y * cs);
                ctx.lineTo(w, y * cs);
                ctx.stroke();
            }
        }

        updateGooSVG();
    }, [gridW, gridH, cellSize, tileRoundness, armThickness, diagBridges, diagBlobSize, showGrid, currentTool, updateGooSVG]);

    // Grid cell helpers
    const getCellFromEvent = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return [0, 0];
        const rect = canvas.getBoundingClientRect();
        const x = Math.floor((e.clientX - rect.left) / cellSize);
        const y = Math.floor((e.clientY - rect.top) / cellSize);
        return [Math.max(0, Math.min(x, gridW - 1)), Math.max(0, Math.min(y, gridH - 1))];
    }, [cellSize, gridW, gridH]);

    // Bresenham line
    const getLineCells = (x0, y0, x1, y1) => {
        const cells = [];
        const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
        const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
        let err = dx - dy;
        while (true) {
            cells.push([x0, y0]);
            if (x0 === x1 && y0 === y1) break;
            const e2 = 2 * err;
            if (e2 > -dy) { err -= dy; x0 += sx; }
            if (e2 < dx) { err += dx; y0 += sy; }
        }
        return cells;
    };

    const getRectCells = (x0, y0, x1, y1) => {
        const cells = [];
        const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
        const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
        for (let y = minY; y <= maxY; y++)
            for (let x = minX; x <= maxX; x++)
                cells.push([x, y]);
        return cells;
    };

    const getEllipseCells = (x0, y0, x1, y1) => {
        const cells = [];
        const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
        const rx = Math.abs(x1 - x0) / 2, ry = Math.abs(y1 - y0) / 2;
        if (rx === 0 && ry === 0) return [[Math.round(cx), Math.round(cy)]];
        const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
        const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
        for (let y = minY; y <= maxY; y++) {
            for (let x = minX; x <= maxX; x++) {
                const ddx = (x - cx) / (rx || 0.5), ddy = (y - cy) / (ry || 0.5);
                if (ddx * ddx + ddy * ddy <= 1.1) cells.push([x, y]);
            }
        }
        return cells;
    };

    // Flood fill
    const floodFill = (sx, sy, fillVal) => {
        const grid = gridRef.current;
        const target = grid[sy][sx];
        if (target === fillVal) return;
        const stack = [[sx, sy]];
        const visited = new Set();
        while (stack.length) {
            const [x, y] = stack.pop();
            const key = `${x},${y}`;
            if (visited.has(key)) continue;
            if (x < 0 || x >= gridW || y < 0 || y >= gridH) continue;
            if (grid[y][x] !== target) continue;
            visited.add(key);
            grid[y][x] = fillVal;
            stack.push([x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]);
        }
    };

    // Mouse handlers
    const handleMouseDown = useCallback((e) => {
        const [x, y] = getCellFromEvent(e);
        const grid = gridRef.current;

        if (currentTool === GOO_TOOLS.FILL) {
            pushUndo();
            floodFill(x, y, grid[y][x] ? 0 : 1);
            render();
            return;
        }

        pushUndo();
        isDrawingRef.current = true;

        if (currentTool === GOO_TOOLS.DRAW || currentTool === GOO_TOOLS.ERASE) {
            grid[y][x] = currentTool === GOO_TOOLS.DRAW ? 1 : 0;
            render();
        } else {
            startCellRef.current = [x, y];
        }
    }, [currentTool, getCellFromEvent, pushUndo, render]);

    const handleMouseMove = useCallback((e) => {
        if (!isDrawingRef.current) return;
        const [x, y] = getCellFromEvent(e);
        const grid = gridRef.current;

        if (currentTool === GOO_TOOLS.DRAW || currentTool === GOO_TOOLS.ERASE) {
            grid[y][x] = currentTool === GOO_TOOLS.DRAW ? 1 : 0;
            render();
        } else if (startCellRef.current) {
            const [sx, sy] = startCellRef.current;
            if (currentTool === GOO_TOOLS.LINE) previewCellsRef.current = getLineCells(sx, sy, x, y);
            else if (currentTool === GOO_TOOLS.RECT) previewCellsRef.current = getRectCells(sx, sy, x, y);
            else if (currentTool === GOO_TOOLS.ELLIPSE) previewCellsRef.current = getEllipseCells(sx, sy, x, y);
            render();
        }
    }, [currentTool, getCellFromEvent, render]);

    const handleMouseUp = useCallback((e) => {
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;

        if (startCellRef.current && (currentTool === GOO_TOOLS.LINE || currentTool === GOO_TOOLS.RECT || currentTool === GOO_TOOLS.ELLIPSE)) {
            const [x, y] = getCellFromEvent(e);
            const [sx, sy] = startCellRef.current;
            const grid = gridRef.current;
            let cells;
            if (currentTool === GOO_TOOLS.LINE) cells = getLineCells(sx, sy, x, y);
            else if (currentTool === GOO_TOOLS.RECT) cells = getRectCells(sx, sy, x, y);
            else cells = getEllipseCells(sx, sy, x, y);

            for (const [cx, cy] of cells) {
                if (cy >= 0 && cy < gridH && cx >= 0 && cx < gridW) grid[cy][cx] = 1;
            }
            previewCellsRef.current = [];
            startCellRef.current = null;
            render();
        }
    }, [currentTool, getCellFromEvent, gridH, gridW, render]);

    const handleMouseLeave = useCallback(() => {
        if (isDrawingRef.current && (currentTool === GOO_TOOLS.DRAW || currentTool === GOO_TOOLS.ERASE)) {
            isDrawingRef.current = false;
        }
    }, [currentTool]);

    // Actions
    const invertCanvas = useCallback(() => {
        pushUndo();
        const grid = gridRef.current;
        for (let y = 0; y < gridH; y++)
            for (let x = 0; x < gridW; x++)
                grid[y][x] = grid[y][x] ? 0 : 1;
        render();
    }, [gridH, gridW, pushUndo, render]);

    const clearCanvas = useCallback(() => {
        pushUndo();
        const grid = gridRef.current;
        for (let y = 0; y < gridH; y++)
            for (let x = 0; x < gridW; x++)
                grid[y][x] = 0;
        render();
    }, [gridH, gridW, pushUndo, render]);

    // Get content bounds
    const getBounds = useCallback(() => {
        const grid = gridRef.current;
        let minX = gridW, minY = gridH, maxX = 0, maxY = 0;
        let hasContent = false;
        for (let y = 0; y < gridH; y++) {
            for (let x = 0; x < gridW; x++) {
                if (grid[y][x]) {
                    hasContent = true;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                }
            }
        }
        if (!hasContent) return { x: 0, y: 0, w: gridW * cellSize, h: gridH * cellSize };
        const pad = 2;
        return {
            x: Math.max(0, minX - pad) * cellSize,
            y: Math.max(0, minY - pad) * cellSize,
            w: (Math.min(gridW, maxX + pad + 1) - Math.max(0, minX - pad)) * cellSize,
            h: (Math.min(gridH, maxY + pad + 1) - Math.max(0, minY - pad)) * cellSize
        };
    }, [gridW, gridH, cellSize]);

    // Build export SVG
    const buildSVGContent = useCallback(() => {
        const bounds = cropExport ? getBounds() : { x: 0, y: 0, w: gridW * cellSize, h: gridH * cellSize };
        const pathD = buildPathData();
        const slope = gooContrast;
        const intercept = gooBias / 100;

        // Expand bounds by blur radius so edge blobs aren't hard-clipped
        const blurPad = gooBlur * 3;
        const vx = bounds.x - blurPad;
        const vy = bounds.y - blurPad;
        const vw = bounds.w + blurPad * 2;
        const vh = bounds.h + blurPad * 2;

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vx} ${vy} ${vw} ${vh}" width="${vw}" height="${vh}">
  <defs>
    <filter id="goo" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="${gooBlur}" result="blur"/>
      <feComponentTransfer in="blur" result="goo">
        <feFuncA type="linear" slope="${slope}" intercept="${intercept}"/>
      </feComponentTransfer>
    </filter>
  </defs>
  <g filter="url(#goo)">
    <path d="${pathD}" fill="#000"/>
  </g>
</svg>`;
    }, [cropExport, getBounds, gridW, gridH, cellSize, buildPathData, gooBlur, gooContrast, gooBias]);

    // Check if grid has content
    const hasContent = useCallback(() => {
        const grid = gridRef.current;
        for (let y = 0; y < gridH; y++)
            for (let x = 0; x < gridW; x++)
                if (grid[y][x]) return true;
        return false;
    }, [gridH, gridW]);

    // Save SVG
    const handleSaveSVG = useCallback(() => {
        if (!hasContent()) {
            message.warning('Draw something first!');
            return;
        }
        const svgString = buildSVGContent();
        const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
        onSaveSVG?.(svgString, svgDataUrl);
        message.success('SVG saved!');
    }, [hasContent, buildSVGContent, onSaveSVG]);

    // Save to punch-out list
    const handleSaveToPunches = useCallback(() => {
        if (!hasContent()) {
            message.warning('Draw something first!');
            return;
        }
        const svgString = buildSVGContent();
        // Generate a small thumbnail for the dropdown
        const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
        onSaveToPunches?.(svgString, svgDataUrl);
    }, [hasContent, buildSVGContent, onSaveToPunches]);

    // Download SVG
    const handleDownloadSVG = useCallback(() => {
        if (!hasContent()) {
            message.warning('Draw something first!');
            return;
        }
        const svg = buildSVGContent();
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'goo-shape.svg';
        a.click();
        URL.revokeObjectURL(url);
        message.success('SVG downloaded!');
    }, [hasContent, buildSVGContent]);

    // Copy SVG
    const handleCopySVG = useCallback(() => {
        if (!hasContent()) {
            message.warning('Draw something first!');
            return;
        }
        navigator.clipboard.writeText(buildSVGContent()).then(() => message.success('SVG copied!'));
    }, [hasContent, buildSVGContent]);

    // Apply grid resize
    const applyGridResize = useCallback((newW, newH) => {
        const oldGrid = gridRef.current;
        const w = Math.max(4, Math.min(64, newW));
        const h = Math.max(4, Math.min(64, newH));
        setGridW(w);
        setGridH(h);
        initGrid(w, h);
        // Copy old data
        for (let y = 0; y < Math.min(oldGrid.length, h); y++)
            for (let x = 0; x < Math.min(oldGrid[y]?.length || 0, w); x++)
                gridRef.current[y][x] = oldGrid[y][x];
    }, [initGrid]);

    // Keyboard shortcuts (scoped)
    useEffect(() => {
        if (!visible) return;
        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            const map = { z: GOO_TOOLS.DRAW, x: GOO_TOOLS.ERASE, c: GOO_TOOLS.FILL, a: GOO_TOOLS.LINE, s: GOO_TOOLS.RECT, d: GOO_TOOLS.ELLIPSE };
            if (map[e.key]) {
                e.preventDefault();
                setCurrentTool(map[e.key]);
            }
            if (e.key === 'u' || (e.metaKey && e.key === 'z')) {
                e.preventDefault();
                undo();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [visible, undo]);

    // Init on open
    useEffect(() => {
        if (!visible) return;
        if (gridRef.current.length === 0 || gridRef.current.length !== gridH || gridRef.current[0]?.length !== gridW) {
            initGrid(gridW, gridH);
        }
        // Defer render to next frame for canvas to be available
        requestAnimationFrame(() => {
            resizeCanvas();
            render();
        });
    }, [visible, gridW, gridH, cellSize]);

    // Re-render on param change
    useEffect(() => {
        if (!visible) return;
        render();
    }, [tileRoundness, armThickness, diagBridges, diagBlobSize, gooBlur, gooContrast, gooBias, showGrid]);

    // Tool button
    const ToolBtn = ({ tool, icon, label, shortcut }) => (
        <Tooltip title={`${label} (${shortcut})`}>
            <Button
                type={currentTool === tool ? 'primary' : 'default'}
                icon={icon}
                onClick={() => setCurrentTool(tool)}
                style={{
                    background: currentTool === tool ? '#58a6ff' : '#21262d',
                    borderColor: currentTool === tool ? '#58a6ff' : '#30363d',
                    color: currentTool === tool ? '#fff' : '#c9d1d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14
                }}
                size="small"
            />
        </Tooltip>
    );

    // Slider row - render inline to avoid remounting on re-render
    const sliderRow = (label, min, max, value, onChange, step) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 90, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider
                min={min} max={max} step={step || 1} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#58a6ff' }, rail: { background: '#30363d' }, handle: { borderColor: '#58a6ff', width: 14, height: 14 } }}
            />
            <Text style={{ color: '#58a6ff', fontSize: 11, fontFamily: 'monospace', minWidth: 28, textAlign: 'right' }}>
                {typeof step === 'number' && step < 1 ? value.toFixed(1) : value}
            </Text>
        </div>
    );

    if (!visible) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 100,
                display: 'flex',
                pointerEvents: 'none',
            }}
        >
            {/* Left: Canvas floating panel */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 12,
                    pointerEvents: 'auto',
                }}
            >
                {/* Drawing tools above canvas */}
                <div style={{
                    display: 'flex',
                    gap: 2,
                    marginBottom: 6,
                    background: 'rgba(22, 27, 34, 0.95)',
                    padding: '4px 6px',
                    borderRadius: 6,
                    border: '1px solid #30363d',
                }}>
                    <ToolBtn tool={GOO_TOOLS.DRAW} icon={<EditOutlined />} label="Draw" shortcut="Z" />
                    <ToolBtn tool={GOO_TOOLS.ERASE} icon={<ClearOutlined />} label="Erase" shortcut="X" />
                    <ToolBtn tool={GOO_TOOLS.FILL} icon={<HighlightOutlined />} label="Fill" shortcut="C" />
                    <ToolBtn tool={GOO_TOOLS.LINE} icon={<LineOutlined />} label="Line" shortcut="A" />
                    <ToolBtn tool={GOO_TOOLS.RECT} icon={<BorderOutlined />} label="Rect" shortcut="S" />
                    <ToolBtn tool={GOO_TOOLS.ELLIPSE} icon={<span style={{ fontSize: 14 }}>◯</span>} label="Ellipse" shortcut="D" />
                    <div style={{ width: 1, background: '#30363d', margin: '0 4px' }} />
                    <Button size="small" icon={<SwapOutlined />} onClick={invertCanvas}
                        style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9', fontSize: 10 }} />
                    <Button size="small" icon={<ClearOutlined />} onClick={clearCanvas}
                        style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9', fontSize: 10 }} />
                    <Button size="small" icon={<UndoOutlined />} onClick={undo}
                        style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9', fontSize: 10 }} />
                </div>

                {/* Goo canvas */}
                <div
                    ref={containerRef}
                    style={{
                        background: '#1a1a2e',
                        borderRadius: 6,
                        border: '1px solid #30363d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 8,
                        position: 'relative',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                    }}
                >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <canvas
                            ref={canvasRef}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            style={{ display: 'block', cursor: 'crosshair', background: '#fff', borderRadius: 2 }}
                        />
                        <svg
                            ref={gooSvgRef}
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                        />
                    </div>
                </div>
            </div>

            {/* Right: Controls panel */}
            <div style={{
                width: 280,
                flexShrink: 0,
                borderLeft: '1px solid #30363d',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                background: '#0d1117',
                pointerEvents: 'auto',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderBottom: '1px solid #30363d',
                    background: '#161b22',
                    flexShrink: 0,
                }}>
                    <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 13 }}>✦ Goo Editor</span>
                    <Button
                        type="text"
                        size="small"
                        onClick={onClose}
                        style={{ color: '#8b949e', fontSize: 11, padding: '2px 6px' }}
                    >
                        ✕
                    </Button>
                </div>

                {/* Scrollable controls */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Tiles & Branches */}
                    <div>
                        <Text style={{ color: '#58a6ff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Tiles & Branches
                        </Text>
                        <div style={{ marginTop: 6 }}>
                            {sliderRow('Tile roundness', 0, 50, tileRoundness, setTileRoundness)}
                            {sliderRow('Arm thickness', 10, 100, armThickness, setArmThickness)}
                            {sliderRow('Diag blob size', 5, 50, diagBlobSize, setDiagBlobSize)}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 90 }}>Diag bridges</Text>
                                <Switch
                                    checked={diagBridges}
                                    onChange={setDiagBridges}
                                    size="small"
                                    style={{ background: diagBridges ? '#58a6ff' : '#30363d' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Goo Filter */}
                    <div>
                        <Text style={{ color: '#58a6ff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Goo Filter
                        </Text>
                        <div style={{ marginTop: 6 }}>
                            {sliderRow('Blur', 0, 30, gooBlur, setGooBlur, 0.5)}
                            {sliderRow('Contrast', 1, 100, gooContrast, setGooContrast)}
                            {sliderRow('Bias', -50, 50, gooBias, setGooBias)}
                        </div>
                    </div>

                    {/* Canvas Settings */}
                    <div>
                        <Text style={{ color: '#58a6ff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Canvas
                        </Text>
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Grid</Text>
                            <InputNumber
                                min={4} max={64} value={gridW}
                                onChange={(v) => applyGridResize(v || 16, gridH)}
                                style={{ width: 52, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                                size="small"
                            />
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>×</Text>
                            <InputNumber
                                min={4} max={64} value={gridH}
                                onChange={(v) => applyGridResize(gridW, v || 16)}
                                style={{ width: 52, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                                size="small"
                            />
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Cell</Text>
                            <InputNumber
                                min={8} max={40} value={cellSize}
                                onChange={(v) => setCellSize(v || 20)}
                                style={{ width: 52, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                                size="small"
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Show grid</Text>
                            <Switch checked={showGrid} onChange={setShowGrid} size="small"
                                style={{ background: showGrid ? '#58a6ff' : '#30363d' }} />
                            <Text style={{ color: '#8b949e', fontSize: 11, marginLeft: 8 }}>Crop export</Text>
                            <Switch checked={cropExport} onChange={setCropExport} size="small"
                                style={{ background: cropExport ? '#58a6ff' : '#30363d' }} />
                        </div>
                    </div>

                    {/* Export Actions */}
                    <div>
                        <Text style={{ color: '#58a6ff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>
                            Actions
                        </Text>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            <Button
                                icon={<SaveOutlined />}
                                onClick={handleSaveSVG}
                                style={{ background: '#238636', borderColor: '#238636', color: '#fff', fontWeight: 500 }}
                            >
                                Save SVG
                            </Button>
                            <Button
                                icon={<ScissorOutlined />}
                                onClick={handleSaveToPunches}
                                style={{ background: '#58a6ff', borderColor: '#58a6ff', color: '#fff', fontWeight: 500 }}
                            >
                                Add Punch
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadSVG}
                                style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                            >
                                Download
                            </Button>
                            <Button
                                icon={<CopyOutlined />}
                                onClick={handleCopySVG}
                                style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                            >
                                Copy SVG
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GooEditorPanel;
